import {
	getRouteApi,
	Link,
	useLocation,
	useRouter,
	useRouterState,
} from "@tanstack/react-router";
import logoLtcaTranspUrl from "@/assets/logo-ltca-academy.png";
import { ArrowIcon } from "../icons/arrow";
import { useQuery } from "@tanstack/react-query";
import { getArticleContent, getEventContent } from "../../api/api";
import { BellIcon } from "../icons/bell";
import { BellDarkIcon } from "../icons/bellDark";
import type { ReactElement } from "react";
import { useOnlineStatusContext } from "../../context/OnlineStatusContext";
import { Skeleton } from "../ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { KursyDoradcaList } from "@/kursy/doradca";
import { KursyRachunkowoscList } from "@/kursy/kurs-rachunkowosc";
import { KursyPopList } from "@/kursy/pop";

function checkTypeAndId(url: string): { pageType: string; id: number } {
	const regex = /^\/(articles|workshops\/\w+)\/(\d+)$/;
	const match = url.match(regex);

	if (match) {
		const pageTypeMapping = {
			articles: "articleContent",
			workshops: "workshopContent",
		};

		return {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			pageType:
				pageTypeMapping[match[1].split("/")[0]] || pageTypeMapping[match[1]], // Transform "article" -> "articleContent", etc.
			id: parseInt(match[2], 10), // Convert the ID to a number
		};
	}

	return { pageType: "", id: 0 };
}

export const NavigationMVP = ({
	counterBadge,
}: {
	counterBadge: number | undefined;
}): ReactElement => {
	const location = useLocation();
	const pageInfo = checkTypeAndId(location.pathname);
	const router = useRouter();
	const state = useRouterState();
	const { isOnline } = useOnlineStatusContext();
	const { isAuthenticated } = useAuth();
	const routeApi = getRouteApi(
		state.matches[3]?.routeId as unknown as "/(app)/_app/study/$studyId"
	);
	const loaderData = routeApi.useLoaderData();

	const { data, isFetching } = useQuery({
		queryKey: [pageInfo.pageType, pageInfo.id],
		queryFn: () =>
			pageInfo.pageType === "workshopContent"
				? getEventContent(pageInfo.id)
				: getArticleContent(pageInfo.id),
		enabled: pageInfo.pageType !== "" && isOnline,
	});

	const kursyAll = [
		...KursyRachunkowoscList,
		...KursyDoradcaList,
		...KursyPopList,
	];

	const generateHeaderText = (): string | ReactElement => {
		const regexWorkshopUrl = /^\/workshops\/\w+\/\d+$/;
		const regexCourseUrl = /^\/course\/([^\/]+)$/; // Regex dla /course/$courseKey
		if (location.pathname === "/articles") {
			return "Aktualności";
		} else if (location.pathname.includes("/articles/")) {
			return data?.title || "";
		} else if (location.pathname === "/workshops/webinar") {
			return "Webinary";
		} else if (location.pathname === "/workshops/training") {
			return "Szkolenia";
		} else if (location.pathname === "/workshops/course") {
			return "Kursy i warsztaty";
		} else if (
			location.pathname.includes("/workshops/") &&
			location.pathname.includes("/auth")
		) {
			return (
				loaderData?.studyWorkshopDetails?.course?.name || (
					<Skeleton className="h-5 w-full rounded-xl m-auto" />
				)
			);
		} else if (regexWorkshopUrl.test(location.pathname)) {
			return (
				data?.nazwa || <Skeleton className="h-5 w-full rounded-xl m-auto" />
			);
		} else if (regexCourseUrl.test(location.pathname)) {
			// Nowa logika dla /course/$courseKey
			const match = location.pathname.match(regexCourseUrl);
			if (match) {
				const courseKey = match[1];
				const foundCourse = kursyAll.find((kurs) => kurs.slug === courseKey);
				return foundCourse?.title || "Kurs";
			}
			return "Kurs";
		} else if (location.pathname.includes("/workshops")) {
			return "Szkolenia, warsztaty, webinary";
		} else if (location.pathname === "/upcoming-events") {
			return "Nadchodzące";
		} else if (location.pathname === "/contact") {
			return "Kontakt";
		} else if (location.pathname === "/courses") {
			return "Kursy";
		} else if (location.pathname === "/courses/pop") {
			return "Podatki od Podstaw";
		} else if (location.pathname === "/courses/kurs-rachunkowosc") {
			return "Rachunkowość";
		} else if (location.pathname === "/courses/kurs-doradca-podatkowy") {
			return "Doradca Podatkowy";
		} else if (location.pathname === "/roadshow") {
			return "Wydarzenia";
		} else if (location.pathname === "/articles-brief") {
			return "Dzisiejsze aktualności";
		} else if (location.pathname === "/user-profil") {
			return "Profil";
		} else if (location.pathname.includes("/study")) {
			return (
				loaderData?.studyWorkshopDetails?.course?.name || (
					<Skeleton className="h-5 w-full rounded-xl m-auto" />
				)
			);
		} else if (location.pathname === "/my-academy") {
			return "Moja akademia";
		} else if (location.pathname === "/offline-videos") {
			return "Oglądaj offline";
		} else if (location.pathname === "/offline-mode") {
			return "Tryb offline";
		} else {
			return "";
		}
	};

	const isHomeHeader =
		location.pathname === "/" ||
		location.pathname === "/notifications" ||
		location.pathname === "/offline-mode";

	return (
		<>
			{isHomeHeader && (
				<div className="flex justify-between items-center w-full">
					<div className="flex items-center gap-2">
						<img alt="LCTA Logo" className="h-14" src={logoLtcaTranspUrl} />
						{import.meta.env.VITE_BUILD_ENV === "development" && (
							<span className="text-xs text-gray-500">DEV</span>
						)}
						{import.meta.env.VITE_BUILD_ENV === "staging" && (
							<span className="text-xs text-gray-500">STG</span>
						)}
					</div>
					{isAuthenticated && location.pathname !== "/offline-mode" && (
						<Link className="absolute right-0 p-5" to="/notifications">
							<div className="relative">
								{location.pathname === "/notifications" ? (
									<BellDarkIcon />
								) : (
									<BellIcon />
								)}
								{counterBadge !== undefined && counterBadge > 0 && (
									<div className="absolute z-10 top-[-10px] right-[-10px] bg-notification text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full">
										{counterBadge}
									</div>
								)}
							</div>
						</Link>
					)}
				</div>
			)}
			{!isHomeHeader && (
					<div className="relative flex w-full text-center">
						<button
							className="absolute top-[-0.9rem] left-[-1.5rem] flex items-center p-5"
							onClick={() => {
								router.history.length > 1
									? router.history.back()
									: router.history.push("/");
							}}
						>
							<ArrowIcon />
						</button>
						{!isFetching && (
							<h3 className="min-w-[50%] max-w-[80%] m-auto text-center font-medium truncate">
								{generateHeaderText()}
							</h3>
						)}
					</div>
				)}
		</>
	);
};
